import React, { useEffect, useRef, useState } from "react";
import * as faceDetection from "@tensorflow-models/face-detection";
import * as tf from "@tensorflow/tfjs-core";
import "@tensorflow/tfjs-backend-webgl";

const CameraPopup = ({ closeModal, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [statusMsg, setStatusMsg] = useState("Checking camera permission...");
  const detectorRef = useRef(null);
  const detectionInterval = useRef(null);
  const hasCaptured = useRef(false); // ✅ Capture guard

  useEffect(() => {
    initCamera();
    return () => stopCamera();
  }, []);

  const initCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = async () => {
          videoRef.current.play();
          setStatusMsg("Loading face detector...");
          await loadFaceDetector();
        };
      }
    } catch (err) {
      console.error("Camera permission error:", err);
      setStatusMsg("Camera permission denied.");
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
    clearInterval(detectionInterval.current);
    hasCaptured.current = false; // reset for future popup use
  };

  const loadFaceDetector = async () => {
    await tf.setBackend("webgl");
    await tf.ready();
    const detector = await faceDetection.createDetector(
      faceDetection.SupportedModels.MediaPipeFaceDetector,
      {
        runtime: "tfjs",
        maxFaces: 1,
      }
    );
    detectorRef.current = detector;
    setStatusMsg("Looking for your face...");
    startFaceDetection();
  };

  const startFaceDetection = () => {
    detectionInterval.current = setInterval(async () => {
      if (hasCaptured.current) return;

      const video = videoRef.current;
      if (!video || video.videoWidth === 0 || video.videoHeight === 0) return;

      const faces = await detectorRef.current.estimateFaces(video);
      if (faces.length > 0) {
        console.log("✅ Face detected!");
        captureSnapshot();
      }
    }, 500);
  };

  const captureSnapshot = () => {
    if (!videoRef.current || !canvasRef.current || hasCaptured.current) return;

    hasCaptured.current = true; // ✅ prevent further calls
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

    const imageData = canvas.toDataURL("image/png");

    stopCamera();
    setStatusMsg("Face captured successfully!");

    setTimeout(() => {
      onCapture(imageData); // 🔒 Only one call will go out now
      closeModal();
    }, 1000);
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {hasCaptured.current ? "Verified" : "Verifying..."}
            </h5>
          </div>
          <div className="modal-body text-center">
            <p>{statusMsg}</p>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-100"
              style={{ maxHeight: "300px", borderRadius: "10px" }}
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
            {statusMsg.includes("denied") && (
              <div className="alert alert-danger mt-2">
                Please allow camera access in browser settings.
              </div>
            )}
            {hasCaptured.current && (
              <div className="alert alert-success mt-2">
                Face captured successfully!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraPopup;